import { useEffect, useState } from 'react';
import { AgentService } from '../services/agentService.ts';
import { AgentAction } from '../components/chat/chat.ts';

interface AgentIntegrationState {
  recentActions: AgentAction[];
  isProcessingAction: boolean;
}

// Global refresh event emitter for cross-component updates
class RefreshEventEmitter extends EventTarget {
  emitRefresh(componentType: string, action?: AgentAction) {
    this.dispatchEvent(new CustomEvent('refresh', { 
      detail: { componentType, action } 
    }));
  }
}

export const globalRefreshEmitter = new RefreshEventEmitter();

export const useAgentIntegration = (componentType: 'calendar' | 'task' | 'budget' | 'vendor') => {
  const [state, setState] = useState<AgentIntegrationState>({
    recentActions: [],
    isProcessingAction: false
  });

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const agentService = AgentService.getInstance();

    // Register listener for this component type
    const handleAction = (action: AgentAction) => {
      console.log(`Agent action received for ${componentType}:`, action);
      
      setState(prev => ({
        ...prev,
        recentActions: [action, ...prev.recentActions.slice(0, 4)], // Keep last 5 actions
        isProcessingAction: false
      }));

      // Trigger a refresh of the component data to fetch updated data from backend
      setRefreshTrigger(prev => prev + 1);

      // Emit global refresh event for other components that might be affected
      globalRefreshEmitter.emitRefresh(componentType, action);

      // Show a brief notification
      showActionNotification(action);
    };

    agentService.onAction(componentType, handleAction);

    // Listen for global refresh events from other components
    const handleGlobalRefresh = (event: CustomEvent) => {
      const { componentType: sourceType, action } = event.detail;
      
      // Refresh if this is a related action type
      if (shouldRefreshForAction(componentType, sourceType, action)) {
        setRefreshTrigger(prev => prev + 1);
      }
    };

    globalRefreshEmitter.addEventListener('refresh', handleGlobalRefresh as EventListener);

    return () => {
      globalRefreshEmitter.removeEventListener('refresh', handleGlobalRefresh as EventListener);
    };
  }, [componentType]);

  // Determine if component should refresh based on action from another component
  const shouldRefreshForAction = (currentType: string, sourceType: string, action?: AgentAction) => {
    if (!action) return false;

    // Calendar should refresh when tasks are created (for due dates)
    if (currentType === 'calendar' && sourceType === 'task') {
      return action.data?.dueDate !== undefined;
    }

    // Tasks should refresh when calendar events affect deadlines
    if (currentType === 'task' && sourceType === 'calendar') {
      return action.data?.category === 'planning' || action.data?.category === 'venue';
    }

    // Budget should refresh when tasks or events are created (for cost tracking)
    if (currentType === 'budget' && (sourceType === 'task' || sourceType === 'calendar')) {
      return action.data?.cost !== undefined || action.data?.category === 'venue';
    }

    return false;
  };

  const showActionNotification = (action: AgentAction) => {
    // Enhanced notification with better styling and positioning
    const notification = document.createElement('div');
    notification.className = `
      fixed top-24 right-6 z-50 max-w-sm w-auto
      bg-white/95 backdrop-blur-xl border border-pink-200/50 
      rounded-xl shadow-lg p-4 
      transform translate-x-full transition-all duration-300 ease-out
    `.trim();
    
    const getActionIcon = (type: string) => {
      switch (type) {
        case 'calendar': return '📅';
        case 'task': return '✅';
        case 'budget': return '💰';
        case 'vendor': return '👥';
        default: return '✨';
      }
    };

    const getActionMessage = (action: AgentAction) => {
    switch (action.type) {
      case 'calendar':
          return `Calendar event "${action.data?.title || 'Event'}" added`;
      case 'task':
          return `Task "${action.data?.title || 'Task'}" created`;
      case 'budget':
          return `Budget updated for ${action.data?.category || 'category'}`;
      case 'vendor':
          return `Vendor action completed`;
      default:
          return 'Action completed';
    }
    };
    
    notification.innerHTML = `
      <div class="flex items-start space-x-3">
        <div class="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm">
          ${getActionIcon(action.type)}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between">
            <p class="text-sm font-medium text-gray-900 truncate">
              ${getActionMessage(action)}
            </p>
            <button class="text-gray-400 hover:text-gray-600 ml-2" onclick="this.parentElement.parentElement.parentElement.style.transform='translateX(100%)'">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            ${new Date().toLocaleTimeString()}
          </p>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Slide in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (notification.parentElement) {
            document.body.removeChild(notification);
          }
        }, 300);
      }
    }, 4000);
  };

  const markActionAsViewed = (actionId: string) => {
    setState(prev => ({
      ...prev,
      recentActions: prev.recentActions.filter(action => action.data.id !== actionId)
    }));
  };

  const clearActions = () => {
    setState(prev => ({
      ...prev,
      recentActions: []
    }));
  };

  // Manual refresh function for components to call when needed
  const forceRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return {
    ...state,
    refreshTrigger, // Components can use this to refetch data when agent makes changes
    markActionAsViewed,
    clearActions,
    forceRefresh
  };
};

// Hook for showing agent status in the UI
export const useAgentStatus = () => {
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [lastActionTime, setLastActionTime] = useState<Date | null>(null);

  useEffect(() => {
    const agentService = AgentService.getInstance();

    // Listen to all action types
    const actionTypes = ['calendar', 'task', 'budget', 'vendor', 'navigation'];
    
    actionTypes.forEach(type => {
      agentService.onAction(type, () => {
        setIsAgentActive(true);
        setLastActionTime(new Date());
        
        // Reset active status after 2 seconds
        setTimeout(() => {
          setIsAgentActive(false);
        }, 2000);
      });
    });

  }, []);

  return {
    isAgentActive,
    lastActionTime
  };
};

// Hook for global refresh state (useful for dashboard-level updates)
export const useGlobalRefresh = () => {
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  useEffect(() => {
    const handleGlobalRefresh = () => {
      setLastRefresh(Date.now());
    };

    globalRefreshEmitter.addEventListener('refresh', handleGlobalRefresh as EventListener);

    return () => {
      globalRefreshEmitter.removeEventListener('refresh', handleGlobalRefresh as EventListener);
    };
  }, []);

  return { lastRefresh };
}; 